import { differenceInCalendarDays, parseISO } from 'date-fns';
import { BlockArchetype } from '../../../models/block';
import type { DashboardBlock, UserBlock } from '../../../models/block';
import { ConnectPreference } from '../../../models/suspectIdentificationBlockData';
import { formatDate } from '../utils';

const BLOCK_DATE_FORMAT = 'MMM D';

export const getAssignedBy = (assignedBy: UserBlock['assigned_by']): string =>
  assignedBy?.full_name?.trim() || assignedBy?.display_name?.trim() || '--';

export const getBlockDateRangeLabel = (
  dateAssigned: string | null | undefined,
  dateEnded: string | null | undefined
): string | null => {
  if (!dateAssigned) return null;

  const assignedLabel = formatDate(dateAssigned, BLOCK_DATE_FORMAT);
  if (!dateEnded) return assignedLabel;

  return `${assignedLabel} - ${formatDate(dateEnded, BLOCK_DATE_FORMAT)}`;
};

// Whole days between a YYYY-MM-DD date and today.
export const getDaysSince = (date: string): number =>
  differenceInCalendarDays(new Date(), parseISO(date));

export const getDaysAgoLabel = (days: number): string => {
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
};

// A dashboard block is assignable if it's a goal or program archetype.
export const isAssignableBlock = (block: DashboardBlock): boolean =>
  block.archetype === BlockArchetype.Goal ||
  block.archetype === BlockArchetype.Program ||
  block.archetype === BlockArchetype.Contraindicated;

export const getRequestedNextStepLabel = (
  connectPreference: ConnectPreference | null
): string => {
  if (connectPreference === ConnectPreference.Book) return 'Meet with a coach';
  if (connectPreference === ConnectPreference.Chat) return 'Chat with a coach';
  if (connectPreference === ConnectPreference.Nothing) {
    return 'Check in with member';
  }

  // This should never happen
  return '--';
};

// Decoding runs after tags are stripped, so encoded markup like `&lt;b&gt;` stays literal text.
const decodeHtmlEntities = (value: string): string =>
  new DOMParser().parseFromString(value, 'text/html').documentElement
    .textContent ?? '';

export const stripHtml = (value: string): string =>
  decodeHtmlEntities(value.replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
